import { useMutation, useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { queryFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { ProductionDeviationModal } from "features/CenterContent/RoleContent/ProductionDeviation/ProductionDeviationModal";
import { ProductionDeviationRequest } from "features/CenterContent/RoleContent/ProductionDeviation/types/requestSchema";
import {
  ProductionDeviationDetailedResponse,
  productionDeviationDetailedSchema,
} from "features/CenterContent/RoleContent/ProductionDeviation/types/responseSchema";
import { responseToForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/transformers";
import { productionDeviationQueryKey } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useProductionDeviation";
import { FC } from "react";
import { useToastOnError } from "shared/components/dataDisplay/useToastOnError";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";

type EditProductionDeviationModalProps = {
  uuid: string;
  onClose: () => void;
};

export const EditProductionDeviationModal: FC<
  EditProductionDeviationModalProps
> = ({ uuid, onClose }) => {
  const countryCode = useCountryCodeForDropsRole();
  const { mutate: post, status } = useMutation({
    mutationFn: ([body]: ProductionDeviationRequest) =>
      queryFnPOST<unknown>({
        url: `${getBackendUrl()}/production-deviation/${uuid}/change`,
        body,
        signal: undefined,
      }).then(() => {
        void queryClient.invalidateQueries({
          queryKey: productionDeviationQueryKey(countryCode),
        });
      }),
  });
  const { data, isError } = useQuery({
    queryKey: ["editProductionDeviation", uuid],
    queryFn: ({ signal }) =>
      axiosClient
        .get<ProductionDeviationDetailedResponse>(
          `${getBackendUrl()}/production-deviation/${uuid}`,
          { signal },
        )
        .then((res) => productionDeviationDetailedSchema.parse(res.data)),
    // when exiting the edit modal we want to immediately remove the old values from the query cache completely to
    // prevent the previous version of the Event to appear in the form before loading the newest version
    // of the Event when editing the Event again
    gcTime: 0,
  });

  useToastOnError(isError, onClose);

  if (!data) return null;

  return (
    <ProductionDeviationModal
      post={post}
      status={status}
      onClose={onClose}
      defaultValues={responseToForm(data)}
    />
  );
};
